// app/src/components/AccountFoot.jsx — the rail-footer account area (handoff: account/credits/
// settings live here, never a top-level tab). The Q-CREDITS meter (credits only — $ is NEVER
// shown to users), the avatar, and the account popover: settings/plan/top-up/billing/language/
// dark-mode/sign-out. The ONE place a $ appears is the top-up pack price (real Stripe money) —
// `account.packPrice` formats it. The UI-locale picker here is DISTINCT from the video Language
// chip (D1-DESIGN §8.2). AccountMenu is exported so mobile can render it in a bottom sheet.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Settings, Star, CircleDollarSign, CreditCard, Moon, Globe, LogOut, Check, ChevronLeft } from 'lucide-react';
import { useDismiss } from '@common/dismiss.js';
import { useI18n } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { useSwitchLocale } from '../lib/locale-sync.jsx';
import { useToast } from '../lib/toast.jsx';

function initial(me) {
  const n = me && (me.display_name || me.email);
  return n ? n.trim().charAt(0).toUpperCase() : '·';
}

// shared menu-row / divider styling (static) — used by the account menu AND the top-up list.
const ROW = { display: 'flex', alignItems: 'center', gap: 11, padding: '9px 11px', borderRadius: 'var(--r-s)', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', font: '600 13px var(--font-body)', color: 'var(--ink)', textAlign: 'start' };
const DIVIDER = { height: 1, background: 'var(--fill-track)', margin: '6px 8px' };

// TopUpList — the credit packs (GET /v1/pricing) + checkout (POST /v1/checkout). Extracted so the
// shell-level top-up sheet (lib/topup.jsx) the out-of-credits gate opens reuses the EXACT same list.
export function TopUpList() {
  const { t, locale } = useI18n();
  const { api } = useSession();
  const toast = useToast();
  const [packs, setPacks] = useState([]);
  useEffect(() => { api.get('pricing').then((r) => { if (r.kind === 'ok') setPacks(r.body.packs || []); }); }, [api]);
  async function topUp(packId) {
    const r = await api.post('checkout', { pack_id: packId });
    if (r.kind === 'ok' && r.body.url) window.location.assign(r.body.url);
    else toast(r.error || t('common.error'));
  }
  // $ is real Stripe money — the one place it shows a user. Intl.NumberFormat is correct in all 24 locales.
  const money = (p) => new Intl.NumberFormat(locale(), { style: 'currency', currency: p.currency || 'USD' }).format((p.usd_cents || 0) / 100);
  return (
    <>
      {packs.length === 0 && <div className="muted" style={{ padding: '4px 11px', fontSize: 12 }}>{t('common.loading')}</div>}
      {packs.map((p) => (
        <button key={p.id} className="q-btn press" onClick={() => topUp(p.id)} style={{ ...ROW, justifyContent: 'space-between' }}>
          <span>{p.label || t('account.creditsN', { credits: p.credits })}</span>
          <span className="mono faint" style={{ fontSize: 11 }}>{t('account.packPrice', { usd: money(p) })}</span>
        </button>
      ))}
    </>
  );
}

// The account menu body — reused by the rail popover (desktop) and the mobile account sheet.
export function AccountMenu({ onClose }) {
  const { t, locale, registry } = useI18n();
  const { auth, me, credits } = useSession();
  const switchLocale = useSwitchLocale(); // D-AUTH §3: i18n + userLang cookie + PUT /v1/me/locale in one breath
  const toast = useToast();
  const [panel, setPanel] = useState(null); // null | 'topup' | 'lang'

  const back = (
    <button className="q-btn" onClick={() => setPanel(null)} style={{ ...ROW, color: 'var(--text-muted)' }}>
      <ChevronLeft size={16} color="var(--text-muted)" strokeWidth={2} />{t('common.back')}
    </button>
  );

  if (panel === 'topup') {
    return (
      <div>
        {back}
        <div style={DIVIDER} />
        <TopUpList />
      </div>
    );
  }

  if (panel === 'lang') {
    return (
      <div>
        {back}
        <div style={DIVIDER} />
        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          {registry.map((l) => (
            <button key={l.tag} className="q-btn" onClick={() => { switchLocale(l.tag); setPanel(null); }} style={{ ...ROW, justifyContent: 'space-between' }}>
              <span>{l.nativeName}</span>
              {locale() === l.tag && <Check size={15} color="var(--mint)" strokeWidth={2.4} />}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const planLabel = (credits && credits.plan && credits.plan.label) || t('account.freePlan');
  return (
    <div>
      <button className="q-btn" onClick={() => toast(t('account.settingsToast'))} style={ROW}><Settings size={16} color="var(--text-muted)" strokeWidth={1.9} />{t('account.settings')}</button>
      <button className="q-btn" onClick={() => toast(t('account.planToast'))} style={ROW}><Star size={16} color="var(--text-muted)" strokeWidth={1.9} />{t('account.plan')}<span style={{ marginInlineStart: 'auto', font: '600 9.5px var(--font-mono)', color: 'var(--text-faint)', background: 'var(--fill-chip)', borderRadius: 'var(--r-pill)', padding: '3px 7px', textTransform: 'uppercase' }}>{planLabel.split(' ')[0]}</span></button>
      <button className="q-btn" onClick={() => setPanel('topup')} style={ROW}><CircleDollarSign size={16} color="var(--text-muted)" strokeWidth={1.9} />{t('account.topUpMenu')}</button>
      <button className="q-btn" onClick={() => toast(t('account.billingToast'))} style={ROW}><CreditCard size={16} color="var(--text-muted)" strokeWidth={1.9} />{t('account.billing')}</button>
      <div style={DIVIDER} />
      <button className="q-btn" onClick={() => setPanel('lang')} style={ROW}><Globe size={16} color="var(--text-muted)" strokeWidth={1.9} />{t('account.language')}<span style={{ marginInlineStart: 'auto' }} className="mono faint">{registry.find((l) => l.tag === locale())?.nativeName}</span></button>
      <div style={DIVIDER} />
      <button className="q-btn" onClick={() => toast(t('account.darkToast'))} style={ROW}><Moon size={16} color="var(--text-muted)" strokeWidth={1.9} />{t('account.dark')}<span style={{ marginInlineStart: 'auto' }}><span style={{ display: 'block', width: 34, height: 20, borderRadius: 999, background: 'var(--border-strong)', position: 'relative' }}><span style={{ position: 'absolute', top: 2, insetInlineStart: 2, width: 16, height: 16, borderRadius: 999, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} /></span></span></button>
      <div style={DIVIDER} />
      <button className="q-btn" onClick={() => { auth.signOut(); onClose && onClose(); }} style={{ ...ROW, color: 'var(--coral)' }}><LogOut size={16} color="var(--coral)" strokeWidth={1.9} />{t('account.signOut')}</button>
    </div>
  );
}

export default function AccountFoot() {
  const { t } = useI18n();
  const { me, credits } = useSession();
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(popRef, close, { enabled: open }); // D-AUTH §6: outside click / Escape dismisses
  const creditsLabel = credits ? credits.balance.toLocaleString() : '…';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {/* Q-CREDITS meter */}
      <div style={{ background: 'var(--paper)', border: '1px solid var(--border)', borderRadius: 'var(--r-card)', padding: 14, display: 'flex', flexDirection: 'column', gap: 11 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="eyebrow" style={{ letterSpacing: '.08em' }}>{t('account.qCredits')}</span>
          <span style={{ font: '700 14px var(--font-body)' }}>{creditsLabel}</span>
        </div>
        <div style={{ height: 7, borderRadius: 999, background: 'var(--credit-track)', overflow: 'hidden', display: 'flex' }}>
          <div style={{ width: '24%', background: 'var(--coral)' }} />
          <div style={{ width: '16%', background: 'var(--tangerine)' }} />
          <div style={{ width: '12%', background: 'var(--yellow)' }} />
          <div style={{ width: '10%', background: 'var(--mint)' }} />
        </div>
        {credits && credits.balance === 0 && (
          <button className="q-btn press" onClick={() => setOpen(true)} style={{ background: 'var(--ink)', color: '#fff', borderRadius: 10, padding: 8, font: '700 12px var(--font-body)', border: 'none' }}>{t('account.topUp')}</button>
        )}
      </div>

      {/* account avatar + popover — the ref wraps popover AND toggle so neither dismisses */}
      <div ref={popRef} style={{ position: 'relative' }}>
        {open && (
          <div style={{ position: 'absolute', insetBlockEnd: 54, insetInline: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-card)', boxShadow: 'var(--shadow-pop)', padding: 7, zIndex: 40 }}>
            <AccountMenu onClose={() => setOpen(false)} />
          </div>
        )}
        <button className="q-btn" onClick={() => setOpen(!open)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 8px', borderRadius: 12, width: '100%', background: 'transparent', border: 'none', cursor: 'pointer' }}>
          <span style={{ width: 30, height: 30, borderRadius: 999, background: 'var(--avatar-grad)', display: 'flex', alignItems: 'center', justifyContent: 'center', font: '700 12px var(--font-body)', color: '#fff' }}>{initial(me)}</span>
          <span style={{ display: 'flex', flexDirection: 'column', textAlign: 'start' }}>
            <span style={{ font: '600 12.5px var(--font-body)' }}>{me ? (me.display_name || me.email) : '…'}</span>
            <span style={{ font: '400 10.5px var(--font-mono)', color: 'var(--text-faint)' }}>{(credits && credits.plan && credits.plan.label) || t('account.freePlan')}</span>
          </span>
          <svg style={{ marginInlineStart: 'auto' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-faintest)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
        </button>
      </div>
    </div>
  );
}
